'use client';

import React, { useState } from 'react';
import { MessageCircle, MapPin, CheckCircle, Gift, AlertTriangle, RotateCcw, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


const timelineEvents = [
  {
    id: 'tl-001',
    time: '14:32',
    type: 'system',
    icon: AlertTriangle,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-500',
    title: 'Reported Lost',
    desc: 'Lost Mode activated for Black Samsonite Suitcase',
    done: true,
  },
  {
    id: 'tl-002',
    time: '14:33',
    type: 'system',
    icon: Gift,
    iconBg: 'bg-accent/10',
    iconColor: 'text-accent',
    title: '£20 Reward Activated',
    desc: 'Finder reward set to £20',
    done: true,
  },
  {
    id: 'tl-003',
    time: '15:08',
    type: 'scan',
    icon: MapPin,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    title: 'Label Scanned',
    desc: 'RT-P19L scanned near Terminal 5 Café',
    done: true,
  },
  {
    id: 'tl-004',
    time: '15:09',
    type: 'location',
    icon: MapPin,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    title: 'Finder Shared Location',
    desc: 'Terminal 5, Heathrow Airport',
    done: true,
  },
  {
    id: 'tl-005',
    time: '15:11',
    type: 'message-finder',
    icon: MessageCircle,
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-600',
    title: 'Finder Message',
    desc: '"Found near the café. Left it with the manager."',
    done: true,
  },
  {
    id: 'tl-006',
    time: '15:12',
    type: 'message-owner',
    icon: MessageCircle,
    iconBg: 'bg-accent/10',
    iconColor: 'text-accent',
    title: 'Your Message',
    desc: '"Thank you! Coming back now."',
    done: true,
  },
  {
    id: 'tl-007',
    time: '15:47',
    type: 'system',
    icon: CheckCircle,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'Suitcase Collected',
    desc: 'Item collected from café manager',
    done: true,
  },
  {
    id: 'tl-008',
    time: '15:48',
    type: 'system',
    icon: RotateCcw,
    iconBg: 'bg-accent/10',
    iconColor: 'text-accent',
    title: 'Return Confirmed',
    desc: 'Alex confirmed the suitcase was returned',
    done: true,
  },
  {
    id: 'tl-009',
    time: '15:48',
    type: 'system',
    icon: Gift,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'Reward Approved',
    desc: '£20 finder reward approved',
    done: true,
  },
  {
    id: 'tl-010',
    time: '15:51',
    type: 'system',
    icon: CheckCircle,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'Reward Paid',
    desc: 'Finder claimed and received £20 reward',
    done: true,
  },
];

const chatMessages = [
  { id: 'msg-001', sender: 'finder', text: 'Found your suitcase near the café. I\'ve left it with the manager.', time: '15:11' },
  { id: 'msg-002', sender: 'owner', text: 'Thank you! Could you leave it with the manager?', time: '15:12' },
  { id: 'msg-003', sender: 'finder', text: 'Sure. I\'ll tell them you\'re coming.', time: '15:13' },
];

export default function RecoveryTimeline() {
  const [activeTab, setActiveTab] = useState<'timeline' | 'chat'>('timeline');
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState(chatMessages);
  const [sending, setSending] = useState(false);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    await new Promise((r) => setTimeout(r, 600));
    setMessages((prev) => [
      ...prev,
      {
        id: `msg-${Date.now()}`,
        sender: 'owner',
        text: newMessage,
        time: '15:52',
      },
    ]);
    setNewMessage('');
    setSending(false);
    toast.success('Message sent securely.');
  };

  return (
    <div className="bg-white rounded-2xl border border-border shadow-card overflow-hidden flex flex-col sticky top-24">
      {/* Recovery summary */}
      <div className="bg-gradient-to-br from-primary to-accent px-5 py-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-accent pulse-dot" />
          <span className="text-white/70 text-xs font-600 uppercase tracking-widest">Recovery Session</span>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-white font-800 text-xl">RECOVERED ✓</p>
            <p className="text-white/70 text-xs mt-1">Black Samsonite Suitcase</p>
          </div>
          <div className="text-right">
            <p className="text-white/60 text-[10px] font-600 uppercase tracking-wide">Recovery Time</p>
            <p className="text-white font-800 text-lg tabular-nums">1h 16m</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="bg-white/15 rounded-xl px-3 py-2 flex-1 text-center">
            <p className="text-white/60 text-[9px] font-600 uppercase tracking-wide">Session Code</p>
            <p className="text-white font-800 text-sm tabular-nums tracking-widest">R7K4-29</p>
          </div>
          <div className="bg-white/15 rounded-xl px-3 py-2 flex-1 text-center">
            <p className="text-white/60 text-[9px] font-600 uppercase tracking-wide">Reward Paid</p>
            <p className="text-white font-800 text-sm tabular-nums">£20</p>
          </div>
        </div>
        <p className="text-white/30 text-[9px] mt-3 text-center">Demo data — simulated recovery session</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {(['timeline', 'chat'] as const).map((tab) => (
          <button
            key={`tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-xs font-700 uppercase tracking-wide transition-colors ${
              activeTab === tab
                ? 'text-accent border-b-2 border-accent bg-accent/5' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'timeline' ? 'Timeline' : 'Secure Chat'}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {activeTab === 'timeline' && (
        <div className="overflow-y-auto max-h-[480px] p-5">
          <div className="relative">
            {timelineEvents.map(({ id, time, icon: Icon, iconBg, iconColor, title, desc, done }, idx) => (
              <div key={id} className="flex gap-3 mb-4 relative">
                {/* Vertical line */}
                {idx < timelineEvents.length - 1 && (
                  <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-border" />
                )}
                <div className={`w-8 h-8 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0 z-10 relative`}>
                  <Icon size={13} className={iconColor} />
                </div>
                <div className="flex-1 pt-0.5 pb-3">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className={`text-xs font-700 ${done ? 'text-foreground' : 'text-muted-foreground'}`}>{title}</p>
                    <span className="text-[10px] text-muted-foreground/60 tabular-nums ml-auto">{time}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chat */}
      {activeTab === 'chat' && (
        <div className="flex flex-col h-[480px]">
          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
            <div className="bg-secondary/50 rounded-xl px-3 py-2 text-center">
              <p className="text-[10px] text-muted-foreground font-500">
                RETURNI Secure Chat — identities are protected. Do not share bank details or home address.
              </p>
            </div>
            {messages.map(({ id, sender, text, time }) => (
              <div
                key={id}
                className={`flex ${sender === 'owner' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                    sender === 'owner' ?'bg-accent text-white rounded-br-sm' :'bg-secondary text-foreground rounded-bl-sm'
                  }`}
                >
                  <p className={`text-xs font-500 ${sender === 'owner' ? 'text-white/70' : 'text-muted-foreground'} mb-0.5`}>
                    {sender === 'owner' ? 'You' : 'Finder'}
                  </p>
                  <p className="text-sm leading-relaxed">{text}</p>
                  <p className={`text-[10px] mt-1 text-right tabular-nums ${sender === 'owner' ? 'text-white/50' : 'text-muted-foreground/60'}`}>
                    {time}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Message input */}
          <div className="border-t border-border p-3 flex items-center gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
              placeholder="Send a secure message..."
              className="flex-1 bg-secondary rounded-full px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none border border-border focus:border-accent focus:bg-white transition-colors"
            />
            <button
              onClick={sendMessage}
              disabled={sending || !newMessage.trim()}
              className="w-9 h-9 bg-accent rounded-full flex items-center justify-center text-white hover:bg-accent/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
              aria-label="Send message"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}